import { useState } from 'react'
import {
  ActivityRow,
  Badge,
  Button,
  Checkbox,
  FormField,
  FormRow,
  FormSection,
  ModalShell,
  StatePanel,
  SummaryStrip,
} from './common'
import styles from './FormBuilder.module.css'

function FormBuilder({ components, onSave, onClose, initialForm = null }) {
  const [formData, setFormData] = useState({
    form_type: initialForm?.form_type || '',
    name: initialForm?.name || '',
    is_active: initialForm?.is_active || false,
    schema: initialForm?.schema || {
      type: 'object',
      properties: {},
      required: []
    },
    ui_schema: initialForm?.ui_schema || {}
  })

  const [fields, setFields] = useState(
    Object.entries(initialForm?.schema?.properties || {}).map(([key, prop]) => ({
      name: key,
      ...prop,
      ui: initialForm?.ui_schema?.[key] || {}
    }))
  )

  const [showFieldDialog, setShowFieldDialog] = useState(false)
  const [editingField, setEditingField] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleAddField = () => {
    setEditingField(null)
    setShowFieldDialog(true)
  }

  const handleEditField = (index) => {
    setEditingField({ ...fields[index], index })
    setShowFieldDialog(true)
  }

  const handleSaveField = (fieldData) => {
    if (editingField !== null && editingField.index !== undefined) {
      // Update existing field
      const newFields = [...fields]
      newFields[editingField.index] = fieldData
      setFields(newFields)
    } else {
      // Add new field
      setFields([...fields, fieldData])
    }
    setShowFieldDialog(false)
    setEditingField(null)
  }

  const handleRemoveField = (index) => {
    setFields(fields.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!formData.form_type || !formData.name) {
      setError('Form type and name are required')
      return
    }

    // Build schema from fields
    const schema = {
      type: 'object',
      properties: {},
      required: []
    }
    
    const ui_schema = {}
    
    fields.forEach(field => {
      const { name, ui, required, ...schemaProps } = field
      schema.properties[name] = schemaProps
      
      if (required) {
        schema.required.push(name)
      }
      
      if (ui && Object.keys(ui).length > 0) {
        ui_schema[name] = ui
      }
    })

    const submitData = {
      ...formData,
      schema,
      ui_schema: Object.keys(ui_schema).length > 0 ? ui_schema : formData.ui_schema
    }

    setSaving(true)
    setError(null)
    
    try {
      await onSave(submitData)
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <ModalShell
      open
      size="xl"
      title={initialForm ? 'Edit form' : 'Create form'}
      description="Define the schema, required fields, and UI hints for this form configuration."
      onClose={onClose}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="form-builder-form"
            disabled={saving}
          >
            {saving ? 'Saving...' : (initialForm ? 'Update Form' : 'Create Form')}
          </Button>
        </>
      }
    >
      {error ? (
        <StatePanel
          state="error"
          title="Form couldn’t be saved"
          description={error}
          compact
          className={styles.errorPanel}
        />
      ) : null}

      <form id="form-builder-form" onSubmit={handleSubmit} className={styles.form}>
          <FormRow>
            <FormField
              label="Form type"
              value={formData.form_type}
              onChange={(e) => setFormData({ ...formData, form_type: e.target.value })}
              placeholder="user_profile"
              required
            />
            <FormField
              label="Form name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="User profile form"
              required
            />
          </FormRow>

          <Checkbox
            label="Set as active form for this type"
            description="Active forms become available to operators using this form type."
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          />

          <SummaryStrip
            label="Form schema summary"
            items={[
              { label: 'Fields', value: fields.length },
              { label: 'Required', value: fields.filter((field) => field.required).length },
              { label: 'Status', value: formData.is_active ? 'Active' : 'Inactive' },
            ]}
          />

          <FormSection
            title="Form fields"
            description="Define the saved schema and the widget used for each operator input."
            actions={(
              <Button
                type="button"
                variant="secondary"
                onClick={handleAddField}
              >
                Add field
              </Button>
            )}
          >

            {fields.length === 0 ? (
              <StatePanel state="empty" title="No fields added" description="Add a field to start building this form schema." compact />
            ) : (
              <div className={styles.fieldsList}>
                {fields.map((field, index) => (
                  <ActivityRow
                    key={`${field.name}-${index}`}
                    title={field.title || field.name}
                    eyebrow={field.name}
                    description={field.description}
                    meta={[
                      { label: 'Type', value: field.type },
                      { label: 'Widget', value: field.ui?.['ui:widget'] || 'Default' },
                    ]}
                    badges={field.required ? <Badge compact>Required</Badge> : null}
                    actions={(
                      <>
                        <Button
                          type="button"
                          variant="secondary"
                          size="compact"
                          onClick={() => handleEditField(index)}
                        >
                          Edit
                        </Button>
                        <Button type="button" variant="ghost" size="compact" onClick={() => handleRemoveField(index)}>
                          Remove
                        </Button>
                      </>
                    )}
                  />
                ))}
              </div>
            )}
          </FormSection>
        </form>
      {showFieldDialog && (
        <FieldEditor
          field={editingField}
          components={components}
          onSave={handleSaveField}
          onClose={() => {
            setShowFieldDialog(false)
            setEditingField(null)
          }}
        />
      )}
    </ModalShell>
  )
}

function FieldEditor({ field, onSave, onClose }) {
  const [fieldData, setFieldData] = useState({
    name: field?.name || '',
    type: field?.type || 'string',
    title: field?.title || '',
    description: field?.description || '',
    required: field?.required || false,
    ...field,
    ui: field?.ui || {}
  })

  const [widget, setWidget] = useState(field?.ui?.['ui:widget'] || '')

  const fieldTypes = [
    { value: 'string', label: 'Text' },
    { value: 'number', label: 'Number' },
    { value: 'integer', label: 'Integer' },
    { value: 'boolean', label: 'Boolean' },
    { value: 'array', label: 'Array' },
    { value: 'object', label: 'Object' }
  ]

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const { name, type, title, description, required } = fieldData
    
    const result = {
      name,
      type,
      required
    }
    
    if (title) result.title = title
    if (description) result.description = description
    
    // Add type-specific properties
    if (type === 'string') {
      if (fieldData.minLength) result.minLength = parseInt(fieldData.minLength)
      if (fieldData.maxLength) result.maxLength = parseInt(fieldData.maxLength)
      if (fieldData.pattern) result.pattern = fieldData.pattern
      if (fieldData.format) result.format = fieldData.format
      if (fieldData.enum) result.enum = fieldData.enum.split(',').map(s => s.trim())
    } else if (type === 'number' || type === 'integer') {
      if (fieldData.minimum !== '') result.minimum = parseFloat(fieldData.minimum)
      if (fieldData.maximum !== '') result.maximum = parseFloat(fieldData.maximum)
    } else if (type === 'array') {
      if (fieldData.minItems) result.minItems = parseInt(fieldData.minItems)
      if (fieldData.maxItems) result.maxItems = parseInt(fieldData.maxItems)
      result.items = { type: fieldData.itemsType || 'string' }
    }
    
    // Add UI schema
    const ui = {}
    if (widget) {
      ui['ui:widget'] = widget
    }
    if (fieldData.placeholder) {
      ui['ui:options'] = ui['ui:options'] || {}
      ui['ui:options'].placeholder = fieldData.placeholder
    }
    
    if (Object.keys(ui).length > 0) {
      result.ui = ui
    }
    
    onSave(result)
  }

  return (
    <ModalShell
      open
      size="lg"
      title={field ? 'Edit field' : 'Add field'}
      description="Field settings determine schema rules and the UI widget that renders this input."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="field-editor-form">
            {field ? 'Update Field' : 'Add Field'}
          </Button>
        </>
      }
    >
        <form id="field-editor-form" onSubmit={handleSubmit} className={styles.fieldForm}>
          <FormRow>
            <FormField
              label="Field name"
              value={fieldData.name}
              onChange={(e) => setFieldData({ ...fieldData, name: e.target.value })}
              placeholder="email"
              required
            />
            <FormField
              label="Field type"
              type="select"
              value={fieldData.type}
              onChange={(e) => setFieldData({ ...fieldData, type: e.target.value })}
              options={fieldTypes}
              required
            />
          </FormRow>

          <FormRow>
            <FormField
              label="Title"
              value={fieldData.title}
              onChange={(e) => setFieldData({ ...fieldData, title: e.target.value })}
              placeholder="Display label for the field"
            />
            <FormField
              label="Widget type"
              type="select"
              value={widget}
              onChange={(e) => setWidget(e.target.value)}
              options={[
                { value: '', label: 'Default' },
                { value: 'text', label: 'Text input' },
                { value: 'textarea', label: 'Textarea' },
                { value: 'select', label: 'Select dropdown' },
                { value: 'radio', label: 'Radio buttons' },
                { value: 'checkbox', label: 'Checkbox' },
                { value: 'date', label: 'Date picker' },
                { value: 'datetime', label: 'Date and time picker' },
                { value: 'email', label: 'Email input' },
                { value: 'url', label: 'URL input' },
                { value: 'password', label: 'Password input' },
                { value: 'number', label: 'Number input' },
                { value: 'resource-select', label: 'Resource select' },
              ]}
            />
          </FormRow>

          {fieldData.type === 'string' ? (
            <>
              <FormRow>
                <FormField label="Minimum length" type="number" value={fieldData.minLength || ''} onChange={(e) => setFieldData({ ...fieldData, minLength: e.target.value })} />
                <FormField label="Maximum length" type="number" value={fieldData.maxLength || ''} onChange={(e) => setFieldData({ ...fieldData, maxLength: e.target.value })} />
              </FormRow>
              <FormField
                label="Format"
                type="select"
                value={fieldData.format || ''}
                onChange={(e) => setFieldData({ ...fieldData, format: e.target.value })}
                options={[
                  { value: '', label: 'None' },
                  { value: 'email', label: 'Email' },
                  { value: 'uri', label: 'URI' },
                  { value: 'date', label: 'Date' },
                  { value: 'date-time', label: 'Date and time' },
                  { value: 'uuid', label: 'UUID' },
                ]}
              />
            </>
          ) : null}

          {(fieldData.type === 'number' || fieldData.type === 'integer') ? (
            <FormRow>
              <FormField label="Minimum" type="number" value={fieldData.minimum || ''} onChange={(e) => setFieldData({ ...fieldData, minimum: e.target.value })} />
              <FormField label="Maximum" type="number" value={fieldData.maximum || ''} onChange={(e) => setFieldData({ ...fieldData, maximum: e.target.value })} />
            </FormRow>
          ) : null}

          <Checkbox
            label="Required field"
            description="Operators must provide a value before submitting this form."
            checked={fieldData.required}
            onChange={(e) => setFieldData({ ...fieldData, required: e.target.checked })}
          />
        </form>
    </ModalShell>
  )
}

export default FormBuilder
